import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { Users } from 'server/shared/collections/users';
import { RoleEnum } from 'server/shared/models/role.enum';
import { User } from 'server/shared/models/user';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { queryParam$ } from '../../../helpers/queryParam$';
import { stringIncludes } from '../../../helpers/stringIncludes';
import { SubscriptionService } from '../subscription.service';

@Component({
  selector: 'app-user-multiselect',
  templateUrl: './user-multiselect.component.html',
  styleUrls: ['./user-multiselect.component.scss'],
})
export class UserMultiselectComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  private suppressParamSync = false;

  @Input() useQueryParam = 'createdBy';
  @Input() label?: string;
  @Input() disabled = false;
  @Input() lightFormField = false;

  selectedCreators: (string | 'ALL')[] = ['ALL'];
  selectSearchTerm$ = new BehaviorSubject<string>('');

  @ViewChild('selectSearchInput', { static: false })
  selectSearchInput?: ElementRef<HTMLInputElement>;

  get selectedCount(): number {
    return this.selectedCreators.includes('ALL')
      ? 0
      : this.selectedCreators.length;
  }

  creatorUsers$: Observable<User[]> = combineLatest([
    this.subscriptionService.dispoUsersOfCustomer.ready$,
    this.subscriptionService.requesterUsersOfCustomer.ready$,
  ]).pipe(
    switchMap(([dispoReady, requesterReady]) => {
      if (!dispoReady || !requesterReady) {
        return of([] as User[]);
      }
      return observeTracker(
        () =>
          Users.find(
            { role: { $in: [RoleEnum.DISPO, RoleEnum.REQUESTER] } },
            { sort: { firstName: 1, lastName: 1 } },
          ).fetch() as User[],
      );
    }),
    rxNgZoneRun(this.ngZone),
    shareReplay(1),
  );

  filteredCreatorUsers$: Observable<User[]> = combineLatest([
    this.creatorUsers$,
    this.selectSearchTerm$,
  ]).pipe(
    map(([creatorUsers, selectSearchTerm]) => {
      if (!selectSearchTerm) {
        return creatorUsers;
      }
      return creatorUsers.filter((user) =>
        stringIncludes(`${user.firstName} ${user.lastName}`, selectSearchTerm),
      );
    }),
    shareReplay(1),
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private subscriptionService: SubscriptionService,
  ) {}

  ngOnInit(): void {
    if (this.useQueryParam) {
      queryParam$(this.route, this.useQueryParam)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((param) => {
          if (this.suppressParamSync) {
            return;
          }
          if (param && param !== 'ALL') {
            this.selectedCreators = param.split(',').filter(Boolean);
          } else {
            this.selectedCreators = ['ALL'];
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectionChange(selectedCreators: (string | 'ALL')[]) {
    this.suppressParamSync = true;

    if (this.selectedCreators.includes('ALL') && selectedCreators.length > 1) {
      selectedCreators = selectedCreators.filter(
        (creator) => creator !== 'ALL',
      );
    }

    if (selectedCreators.includes('ALL')) {
      this.selectedCreators = ['ALL'];
    } else if (selectedCreators.length === 0) {
      this.selectedCreators = ['ALL'];
    } else {
      this.selectedCreators = selectedCreators.filter(
        (creator) => creator !== 'ALL',
      );
    }

    if (this.useQueryParam) {
      const paramValue = this.selectedCreators.includes('ALL')
        ? null
        : this.selectedCreators.join(',');
      this.updateQueryParam(paramValue);
    }

    setTimeout(() => {
      this.suppressParamSync = false;
    }, 0);
  }

  onSelectOpenedChange(opened: boolean) {
    if (opened) {
      setTimeout(() => this.selectSearchInput?.nativeElement.focus());
    } else {
      this.selectSearchTerm$.next('');
    }
  }

  trackByUserId(_index: number, user: User): string {
    return user._id;
  }

  private updateQueryParam(paramValue: string | null) {
    const queryParams: Record<string, string | null> = {};
    queryParams[this.useQueryParam] = paramValue;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
